import { request } from "../utils/request";
import { Job } from "./Jobs";
import { Profile } from "./Profiles";
import { BACKEND_BASE_URL } from "./Resolver";
import { TemplateSetAggregate } from "./TemplateSets";

export type RegisterAccounts = "primary" | "secondary";

export enum BackfillSearchOption {
  PARAMS = "PARAMS",
  CREATIVE_ID = "CREATIVE_ID",
  JOB_ID = "JOB_ID",
}

export enum OperationType {
  REGISTER = "register",
  TRANSCODE = "transcode",
  DEACTIVATE = "deactivate",
}

export type OperationSearchParams = {
  start: string;
  end: string;
  starttime: string;
  endtime: string;
  statuses: string[];
  sets: string[];
};

export class JobOperation {
  baseURL = `${BACKEND_BASE_URL}/api/v1/`;
  upsertFields = [
    "ids",
    "operation",
    "accounts",
    "primary_regions",
    "secondary_regions",
    "overwrite",
    "useDefaultProfiles",
    "profiles",
    "profile",
    "prefix",
  ];

  id = "";
  ids: string[];
  accounts: RegisterAccounts[];
  operation: OperationType;
  primary_regions: string[];
  secondary_regions: string[];
  overwrite: boolean;
  useDefaultProfiles: boolean;
  backfillSearchOption: BackfillSearchOption | null;
  creative_ids: string[];
  profiles: Profile[] = [];
  backfill = false;
  params: OperationSearchParams | null;

  constructor({
    ids,
    accounts,
    primary_regions,
    secondary_regions,
    overwrite,
    useDefaultProfiles,
    backfillSearchOption,
    profiles,
    operation,
    creative_ids,
    backfill,
    params,
  }: {
    ids?: string[];
    accounts: RegisterAccounts[];
    primary_regions: string[];
    secondary_regions: string[];
    overwrite: boolean;
    useDefaultProfiles: boolean;
    backfillSearchOption?: BackfillSearchOption | null;
    profiles: Profile[];
    operation: OperationType;
    creative_ids?: string[];
    backfill?: boolean;
    params?: OperationSearchParams | null;
  }) {
    this.ids = ids ? ids : [];
    this.accounts = accounts;
    this.primary_regions = primary_regions;
    this.secondary_regions = secondary_regions;
    this.overwrite = overwrite;
    this.useDefaultProfiles = useDefaultProfiles;
    this.backfillSearchOption = backfillSearchOption || null;
    this.profiles = profiles;
    this.operation = operation;
    this.creative_ids = creative_ids ? creative_ids : [];
    this.backfill = backfill || false;
    this.params = params || null;
  }

  validProfiles() {
    return !(!this.useDefaultProfiles && this.profiles.length === 0);
  }

  validForBackfill(): boolean {
    let validSearch = false;
    switch (this.backfillSearchOption) {
      case BackfillSearchOption.CREATIVE_ID:
        validSearch = this.creative_ids.length > 0;
        break;
      case BackfillSearchOption.JOB_ID:
        validSearch = this.ids.length > 0;
        break;
      case BackfillSearchOption.PARAMS:
        validSearch = this.params != null;
        break;
    }

    if (this.operation === OperationType.DEACTIVATE) {
      return validSearch;
    }

    const validProfiles = this.profiles.length > 0;
    const validAccounts = this.accounts.length > 0;
    const validPrimaryRegions =
      !this.accounts.includes("primary") ||
      (this.accounts.includes("primary") && this.primary_regions.length > 0);
    const validSecondaryRegions =
      !this.accounts.includes("secondary") ||
      (this.accounts.includes("secondary") && this.secondary_regions.length > 0);

    return validSearch && validProfiles && validAccounts && validPrimaryRegions && validSecondaryRegions;
  }

  getUpsertJSON() {
    // the profiles field cannot be included in the payload if useDefaultProfiles is set to true
    let filteredUpsertFields = this.upsertFields.filter(
      (field: string) => !(field === "profiles" && this.useDefaultProfiles),
    );
    if (this.backfill) {
      filteredUpsertFields = this.upsertFields.filter((field) => field !== "ids");
    }
    if (this.backfillSearchOption === BackfillSearchOption.JOB_ID) {
      filteredUpsertFields.push("ids", this.ids.join(","));
    } else if (this.backfillSearchOption === BackfillSearchOption.CREATIVE_ID) {
      filteredUpsertFields.push("creative_ids", this.creative_ids.join(","));
    }

    if (!this.overwrite) {
      filteredUpsertFields.push("profiles", this.profiles.join(","));
    }

    // deactivate endpoint only takes either creative_ids or ids (job_ids)
    filteredUpsertFields =
      this.operation === OperationType.DEACTIVATE
        ? filteredUpsertFields.filter((field: string) => field === "ids" || field === "creative_ids")
        : filteredUpsertFields;

    return JSON.stringify(this, filteredUpsertFields);
  }

  getSuccessMessage(): string {
    switch (this.operation) {
      case OperationType.TRANSCODE:
        return "Successfully started transcoding";
      case OperationType.DEACTIVATE:
        return "Successfully started job deactivation";
      case OperationType.REGISTER:
        return "Successfully started registering jobs";
    }
  }

  getSearchQueryParams(): {} {
    const defaultParams = { page: 1, limit: 1 };
    if (this.backfillSearchOption === BackfillSearchOption.JOB_ID) {
      return { ...defaultParams, jobids: this.ids.join(",") };
    } else if (this.backfillSearchOption === BackfillSearchOption.CREATIVE_ID) {
      return { ...defaultParams, creativeids: this.creative_ids.join(",") };
    } else {
      return { ...defaultParams, ...this.params };
    }
  }

  getURL(): URL {
    let path = this.backfill ? "backfill" : "jobs/transcode";
    let searchParams = new URLSearchParams();
    // filter based search so include query params
    if (this.backfillSearchOption === BackfillSearchOption.PARAMS) {
      searchParams = new URLSearchParams({
        start: this.params?.start!,
        end: this.params?.end!,
        starttime: this.params?.starttime!,
        endtime: this.params?.endtime!,
      });
      this.params!.statuses.map((status: string) => searchParams.append("status", status));
      this.params!.sets.map((setID: string) => searchParams.append("setid", setID));
    }
    switch (this.operation) {
      case OperationType.TRANSCODE:
      case OperationType.REGISTER:
        if (
          this.backfillSearchOption === BackfillSearchOption.JOB_ID ||
          this.backfillSearchOption === BackfillSearchOption.CREATIVE_ID
        ) {
          path = "jobs/backfill";
        }
        break;
      case OperationType.DEACTIVATE:
        if (this.backfillSearchOption === BackfillSearchOption.CREATIVE_ID) {
          path = "jobs/creatives/deactivation";
        } else if (this.backfillSearchOption === BackfillSearchOption.JOB_ID) {
          path = "jobs/deactivation";
        } else if (this.backfillSearchOption === BackfillSearchOption.PARAMS) {
          path = "deactivation";
        }
        break;
    }
    const url = new URL(this.baseURL + path);
    url.search = searchParams.toString();
    return url;
  }

  static getProfilesForRetry(
    job: Job,
    availableProfiles: Profile[],
    availableSets: TemplateSetAggregate[],
  ): Profile[] {
    const templatesInJob = job!.tasks?.map((task) => task.template) ?? [];
    let profiles: Profile[] = templatesInJob
      ? availableProfiles.filter((profile) => templatesInJob.includes(profile.profile))
      : [];

    if (profiles.length === 0) {
      profiles =
        availableSets
          .find((s) => s.id === job!.set_id)
          ?.templates?.map((t) => {
            return { profile: t.name, prefix: t.prefix };
          }) ?? [];
    }

    return profiles;
  }

  async create() {
    await request(
      this.getURL(),
      { method: "POST", body: this.getUpsertJSON() },
      true,
      this.getSuccessMessage(),
    );
  }

  static async retry(
    jobID: string,
    profiles: Profile[],
    registerAccounts: RegisterAccounts[],
    primaryRegions: string[],
    secondaryRegions: string[],
  ) {
    await new JobOperation({
      ids: [jobID],
      accounts: registerAccounts,
      primary_regions: primaryRegions,
      secondary_regions: secondaryRegions,
      overwrite: true,
      useDefaultProfiles: false,
      profiles: profiles,
      operation: OperationType.TRANSCODE,
    }).create();
  }
}
